import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, hasSession, hasSessionHint, setFallbackToken } from "./api";

const AuthContext = createContext(null);
const RENEW_EVERY_MS = 25 * 60 * 1000; // access tokens last 60 min

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  // If the session cookie didn't stick (cross-site iframe / third-party cookies
  // blocked), keep the token for this tab and authenticate with a bearer header.
  const adoptSession = useCallback((data) => {
    if (!hasSessionHint() && data?.token) setFallbackToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const fetchMe = useCallback(async () => {
    if (!hasSession()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Silent renewal — keeps long-lived sessions alive without a login screen.
  useEffect(() => {
    const renew = async () => {
      if (!user || !hasSession()) return;
      try {
        const { data } = await api.post("/auth/refresh");
        adoptSession(data);
      } catch { /* interceptor handles a dead session */ }
    };
    const onVisible = () => { if (document.visibilityState === "visible") renew(); };
    timer.current = setInterval(renew, RENEW_EVERY_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, adoptSession]);

  const login = async (email, password, remember = true) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    return adoptSession(data);
  };
  const signup = async (email, password, name) => {
    const { data } = await api.post("/auth/signup", { email, password, name });
    return adoptSession(data);
  };
  const resetPassword = async (token, password) => {
    const { data } = await api.post("/auth/reset-password", { token, password });
    return adoptSession(data);
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* cookies cleared client-side anyway */ }
    setFallbackToken(null);
    setUser(null);
    window.location.href = "/login";
  };
  const logoutEverywhere = async () => {
    try { await api.post("/auth/logout-all"); } catch { /* session may already be gone */ }
    setFallbackToken(null);
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, signup, logout, logoutEverywhere, resetPassword, refresh: fetchMe,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
